import React, { useState, useEffect, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import API from '../services/api';
import { todayEastern } from '../dateUtils';
import { hasArea } from '../services/auth';
import ProfileUpdateRequestModal from '../components/ProfileUpdateRequestModal';
import CardsOnFilePanel from '../components/CardsOnFilePanel';
import AddressUpdateLinkModal from '../components/AddressUpdateLinkModal';
import { fieldLabels } from '../profileUpdateFields';

// Helper function to safely get signature data regardless of field names
function getSignatureData(form, type) {
  if (!form) return '';
  
  let sigData = '';
  
  if (type === 'consent') {
    sigData = form.consent_signature || 
              form.consentSignatureData || 
              (form.data_json && form.data_json.consentSignatureData) || 
              form.signature || 
              '';
  } else if (type === 'cancel') {
    sigData = form.cancel_signature || 
              form.cancelSignatureData || 
              (form.data_json && form.data_json.cancelSignatureData) || 
              form.signature || 
              '';
  } else if (type === 'final') {
    sigData = form.final_signature || 
              form.finalSignatureData || 
              (form.data_json && form.data_json.finalSignatureData) || 
              form.signature || 
              '';
  }
  
  return sigData;
}

// Utility functions for formatting
function formatSessionDate(rawDate) {
  if (!rawDate) return '';
  const datePart = String(rawDate).split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
}

function formatCurrency(amount) {
  return `$${parseFloat(amount || 0).toFixed(2)}`;
}

function getPaymentStyle(paymentType) {
  const lower = (paymentType || '').toLowerCase();
  let bg = 'lightgray';
  let fg = '#000';
  if (lower.includes('cash')) {
    bg = 'green';
    fg = '#fff';
  } else if (lower.includes('card')) {
    bg = 'blue';
    fg = '#fff';
  } else if (lower.includes('check')) {
    bg = 'yellow';
    fg = '#000';
  }
  return {
    backgroundColor: bg,
    color: fg,
    padding: '2px 6px',
    borderRadius: '4px',
    display: 'inline-block',
  };
}

function getStatusBadge(status) {
  const statusColors = {
    'PAID': { bg: 'var(--success-color)', color: 'white' },
    'UNPAID': { bg: 'var(--error-color)', color: 'white' },
    'DEBT': { bg: '#fd7e14', color: 'white' },
    'SUBMITTED': { bg: '#6f42c1', color: 'white' }
  };
  
  const style = statusColors[status] || { bg: 'var(--text-muted)', color: 'white' };
  
  return {
    backgroundColor: style.bg,
    color: style.color,
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: 'bold',
    display: 'inline-block'
  };
}

function AdminClientProfilePage() {
  // Client management
  const [allClients, setAllClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [editedClientData, setEditedClientData] = useState({});
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showMergeHistory, setShowMergeHistory] = useState(false);

  // Address-update link (client updates their own address/contact info)
  const [showAddressLinkModal, setShowAddressLinkModal] = useState(false);

  // Profile update requests (ask the therapist to fill in fields, e.g. dx code)
  const [showUpdateRequestModal, setShowUpdateRequestModal] = useState(false);
  const [updateRequestStatus, setUpdateRequestStatus] = useState(null); // { open, recentCompleted }

  // Billing history
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingFilters, setBillingFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'ALL',
    searchTerm: ''
  });
  const [billingPage, setBillingPage] = useState(0);
  const [hasBillingMore, setHasBillingMore] = useState(false);

  // Forms management
  const [localForms, setLocalForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showFormPreview, setShowFormPreview] = useState(false);

  // Statistics
  const [clientStats, setClientStats] = useState({});
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);

  // Modal states
  const [showClientEditor, setShowClientEditor] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  // Store therapist info separately since it's not in the initial client list
  const [clientTherapists, setClientTherapists] = useState({});

  // Helper function to get therapist name - now uses backend data first
  const getClientTherapist = (client) => {
    if (!client) {
      console.log('getClientTherapist: No client provided');
      return null;
    }
    
    console.log('getClientTherapist called for:', client.client_name);
    console.log('Client object keys:', Object.keys(client));
    console.log('therapist_first_name:', client.therapist_first_name);
    console.log('therapist_last_name:', client.therapist_last_name);
    
    // First check if we have it from the backend (new approach)
    if (client.therapist_first_name && client.therapist_last_name) {
      const therapistName = `${client.therapist_first_name} ${client.therapist_last_name}`;
      console.log('✅ Found therapist from backend:', therapistName);
      return therapistName;
    }
    
    // Fallback to stored data from billing history
    if (clientTherapists[client.id]) {
      console.log('✅ Found therapist from stored data:', clientTherapists[client.id]);
      return clientTherapists[client.id];
    }
    
    // Final fallback to billing history lookup
    if (billingHistory.length > 0) {
      const clientSessions = billingHistory.filter(session => 
        session.client_name === client.client_name
      );
      
      if (clientSessions.length > 0) {
        const sortedSessions = clientSessions.sort((a, b) => 
          new Date(b.session_date) - new Date(a.session_date)
        );
        
        const mostRecentSession = sortedSessions[0];
        if (mostRecentSession?.therapist_first_name && mostRecentSession?.therapist_last_name) {
          const therapistName = `${mostRecentSession.therapist_first_name} ${mostRecentSession.therapist_last_name}`;
          console.log('✅ Found therapist from billing history:', therapistName);
          
          // Store it for future use
          setClientTherapists(prev => ({
            ...prev,
            [client.id]: therapistName
          }));
          
          return therapistName;
        }
      }
    }
    
    console.log('❌ No therapist found for client:', client.client_name);
    return null;
  };

  // Use the original allClients since they don't include therapist info
  const clientsWithTherapists = allClients;

  useEffect(() => {
    fetchAllPortalClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      // Reset states first
      setBillingHistory([]);
      setLocalForms([]);
      setClientStats({});
      setBillingPage(0);
      
      // Then fetch data
      fetchClientBillingHistory();
      fetchClientForms();
      fetchClientStats();
    }
  }, [selectedClient]); // Remove billingFilters from dependency array

  // Extract therapist info when billing history is loaded
  useEffect(() => {
    if (selectedClient && billingHistory.length > 0) {
      const therapistName = getClientTherapist(selectedClient.id, selectedClient.client_name);
      if (therapistName && !clientTherapists[selectedClient.id]) {
        setClientTherapists(prev => ({
          ...prev,
          [selectedClient.id]: therapistName
        }));
      }
    }
  }, [billingHistory, selectedClient]);

  // Separate useEffect for billing filters
  useEffect(() => {
    if (selectedClient && (billingFilters.startDate || billingFilters.endDate || billingFilters.status !== 'ALL')) {
      fetchClientBillingHistory();
    }
  }, [billingFilters]);

  // Client management functions
  const fetchAllPortalClients = async () => {
    try {
      const res = await API.get('/admin/clients');
      console.log('=== CLIENT DATA DEBUG ===');
      console.log('Raw API response:', res.data);
      if (res.data && res.data.length > 0) {
        console.log('First client:', res.data[0]);
        console.log('Has therapist_first_name?', !!res.data[0].therapist_first_name);
        console.log('Has therapist_last_name?', !!res.data[0].therapist_last_name);
      }
      console.log('=== END DEBUG ===');
      setAllClients(res.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Remove the fetchClientTherapistInfo function since we don't need it anymore
  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setActiveTab('overview');
    setBillingPage(0);

    // Reset editing state
    setIsEditingClient(false);
    setEditedClientData({});

    // Reset stats
    setClientStats({});

    fetchUpdateRequestStatus(client.id);
  };

  const fetchUpdateRequestStatus = async (clientId) => {
    setUpdateRequestStatus(null);
    if (!hasArea('client_management')) return;
    try {
      const res = await API.get('/admin/profile-update-requests', { params: { clientId } });
      setUpdateRequestStatus(res.data);
    } catch (error) {
      console.error('Error fetching profile update request status:', error);
    }
  };

  const cancelUpdateRequest = async () => {
    const openReq = updateRequestStatus?.open;
    if (!openReq) return;
    try {
      await API.delete(`/admin/profile-update-requests/${openReq.id}`);
      fetchUpdateRequestStatus(selectedClient.id);
    } catch (error) {
      console.error('Error cancelling profile update request:', error);
      alert('Error cancelling request');
    }
  };

  const startEditingClient = () => {
    setEditedClientData({
      clientName: selectedClient.client_name || '',
      phone: selectedClient.phone || '',
      email: selectedClient.email || '',
      emergencyContactName: selectedClient.emergency_contact_name || '',
      emergencyContactPhone: selectedClient.emergency_contact_phone || '',
      isMinor: selectedClient.is_minor || false,
      guardianName: selectedClient.guardian_name || '',
      guardianPhone: selectedClient.guardian_phone || '',
      diagnosisCode: selectedClient.diagnosis_code || '',
      billingEmail: selectedClient.billing_email || '',
      billingAddress: selectedClient.billing_address || ''
    });
    setIsEditingClient(true);
    setShowClientEditor(true);
  };

  const saveClientEdits = async () => {
    try {
      await API.put(`/admin/clients/${selectedClient.id}`, editedClientData);
      
      // Refresh client data
      const updatedClient = await API.get(`/admin/clients/${selectedClient.id}`);
      setSelectedClient(updatedClient.data);
      
      // Update clients list
      setAllClients(prev => prev.map(c => 
        c.id === selectedClient.id 
          ? { ...c, client_name: editedClientData.clientName }
          : c
      ));
      
      setIsEditingClient(false);
      setShowClientEditor(false);
      alert('Client updated successfully!');
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Error updating client information');
    }
  };

  // Billing history functions
  const fetchClientBillingHistory = async (resetPage = true) => {
    if (!selectedClient) return;
    
    try {
      let pageToUse = resetPage ? 0 : billingPage;
      if (resetPage) setBillingPage(0);

      let url = `/admin/daily-report/history?limit=21&offset=${pageToUse * 20}`;
      
      // Add client filter - this is the key part
      url += `&searchTerm=${encodeURIComponent(selectedClient.client_name)}`;
      
      if (billingFilters.startDate) url += `&start=${billingFilters.startDate}`;
      if (billingFilters.endDate) url += `&end=${billingFilters.endDate}`;
      if (billingFilters.status !== 'ALL') url += `&status=${billingFilters.status}`;

      const res = await API.get(url);
      const data = res.data || [];
      
      // Filter to ensure we only get sessions for this specific client
      const clientSessions = data.filter(session => 
        session.client_name === selectedClient.client_name
      );
      
      const weHaveMore = clientSessions.length > 20;
      const first20 = clientSessions.slice(0, 20);

      if (resetPage) {
        setBillingHistory(first20);
      } else {
        setBillingHistory(prev => [...prev, ...first20]);
      }
      setHasBillingMore(weHaveMore);
    } catch (error) {
      console.error('Error fetching billing history:', error);
    }
  };

  const fetchClientForms = async () => {
    if (!selectedClient) return;
    
    try {
      const res = await API.get(`/forms/admin/intake-data/${selectedClient.id}`);
      setLocalForms(res.data || []);
    } catch (error) {
      console.error('Error fetching client forms:', error);
      setLocalForms([]);
    }
  };

  const fetchClientStats = async () => {
    if (!selectedClient || isLoadingStats) return;
    
    setIsLoadingStats(true);
    try {
      console.log(`Fetching stats for client ID: ${selectedClient.id}, Name: ${selectedClient.client_name}`);
      const res = await API.get(`/admin/clients/${selectedClient.id}/statistics`);
      console.log('Stats response:', res.data);
      setClientStats(res.data);
    } catch (error) {
      console.error('Error fetching client statistics:', error);
      // Set empty stats on error
      setClientStats({
        totalSessions: 0,
        totalBilled: 0,
        totalCollected: 0,
        outstandingBalance: 0,
        paidSessions: 0,
        unpaidSessions: 0,
        debtSessions: 0,
        paymentRate: 0
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Invoice generation
  const generateInvoice = async () => {
    if (!selectedClient) return;
    
    try {
      const startDate = billingFilters.startDate || '2020-01-01';
      const endDate = billingFilters.endDate || todayEastern();
      
      const res = await API.get('/admin/client-invoice', {
        params: {
          clientId: selectedClient.id,
          startDate,
          endDate
        }
      });
      
      setInvoiceData(res.data);
      setShowInvoiceModal(true);
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Error generating invoice');
    }
  };

  // Diagnostic function
  const runDiagnostic = async () => {
    if (!selectedClient) return;
    
    try {
      const res = await API.get(`/admin/clients/${selectedClient.id}/diagnostic`);
      setDiagnosticData(res.data);
      setShowDiagnostic(true);
    } catch (error) {
      console.error('Error running diagnostic:', error);
      alert('Error running diagnostic');
    }
  };

  // Removed delete session functionality - this is now view-only

  // Form handling
  const determineFormType = (formData) => {
    const dataJson = formData.data_json || {};
    
    if (dataJson.consentDate && dataJson.clientName) {
      return "Intake Form";
    }
    
    if (dataJson.storedFields && Array.isArray(dataJson.storedFields)) {
      return "Acuity Intake";
    }
    
    if (dataJson.basicInfo) {
      return "Manual Intake";
    }
    
    return "Client Form";
  };

  const handleViewForm = (form) => {
    setSelectedForm(form);
    setShowFormPreview(true);
  };

  // Filtering and display - now includes therapist search from backend data
  const displayedClients = searchTerm
    ? allClients.filter((client) => {
        const therapistName = getClientTherapist(client);
        return client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
               (client.phone && client.phone.includes(searchTerm)) ||
               (therapistName && therapistName.toLowerCase().includes(searchTerm.toLowerCase()));
      })
    : allClients;

  // Styles
  const containerStyle = {
    display: 'flex',
    minHeight: 'calc(100vh - 64px)',
    fontFamily: 'Arial, sans-serif',
    flexWrap: 'wrap'
  };

  const sidebarStyle = {
    width: '300px',
    flex: '0 0 300px',
    maxWidth: '100%',
    borderRight: '1px solid var(--border-color)',
    padding: '1rem',
    backgroundColor: 'var(--surface-muted)',
    overflowY: 'auto'
  };

  const mainContentStyle = {
    flex: '1 1 320px',
    padding: '1rem',
    overflowY: 'auto',
    minWidth: 0
  };

  const clientListItemStyle = (isSelected) => ({
    padding: '0.75rem',
    margin: '0.25rem 0',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: isSelected ? 'var(--primary-color)' : 'white',
    color: isSelected ? 'white' : 'black',
    border: '1px solid var(--border-color)',
    transition: 'all 0.2s'
  });

  const tabStyle = (isActive) => ({
    padding: '0.75rem 1.5rem',
    backgroundColor: isActive ? 'var(--primary-color)' : 'var(--surface-muted)',
    color: isActive ? 'white' : 'black',
    border: 'none',
    borderRadius: '4px 4px 0 0',
    cursor: 'pointer',
    marginRight: '0.25rem'
  });

  const cardStyle = {
    backgroundColor: 'white',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#fff',
    border: '1px solid var(--border-color)',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
    zIndex: 9999,
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 9998
  };

  // Render functions
  const renderClientOverview = () => {
    const therapistName = getClientTherapist(selectedClient);

    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Client Information</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowAddressLinkModal(true)}
              style={{
                backgroundColor: 'var(--surface-muted)',
                color: 'var(--text-color)',
                border: '1px solid var(--border-color)',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Send Address Link
            </button>
            {hasArea('client_management') && (
              <button
                onClick={() => setShowUpdateRequestModal(true)}
                style={{
                  backgroundColor: 'var(--surface-muted)',
                  color: 'var(--text-color)',
                  border: '1px solid var(--border-color)',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Request Update
              </button>
            )}
            <button
              onClick={startEditingClient}
              style={{
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Edit Client
            </button>
          </div>
        </div>

        {updateRequestStatus?.open && (
          <div style={{
            background: '#fff3cd', border: '1px solid #ffe69c', color: '#664d03',
            borderRadius: '6px', padding: '0.5rem 0.75rem', marginBottom: '1rem', fontSize: '0.9rem'
          }}>
            <strong>Update requested</strong> from {getClientTherapist(selectedClient) || 'therapist'} on{' '}
            {new Date(updateRequestStatus.open.created_at).toLocaleDateString()}:{' '}
            {fieldLabels(updateRequestStatus.open.requested_fields)}
            {updateRequestStatus.open.note && <> — “{updateRequestStatus.open.note}”</>}
            <button
              onClick={cancelUpdateRequest}
              style={{
                marginLeft: '0.75rem', background: 'none', border: 'none', color: '#664d03',
                textDecoration: 'underline', cursor: 'pointer', fontSize: '0.85rem', padding: 0
              }}
            >
              Cancel request
            </button>
          </div>
        )}
        {!updateRequestStatus?.open && updateRequestStatus?.recentCompleted && (
          <div style={{
            background: '#d1e7dd', border: '1px solid #a3cfbb', color: '#0a3622',
            borderRadius: '6px', padding: '0.5rem 0.75rem', marginBottom: '1rem', fontSize: '0.9rem'
          }}>
            <strong>Update fulfilled</strong> on{' '}
            {new Date(updateRequestStatus.recentCompleted.completed_at).toLocaleDateString()}:{' '}
            {fieldLabels(updateRequestStatus.recentCompleted.requested_fields)}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <p><strong>Name:</strong> {selectedClient.client_name}</p>
            <p><strong>Primary Therapist:</strong> {therapistName || 'Not assigned'}</p>
            <p><strong>Phone:</strong> {selectedClient.phone || 'Not provided'}</p>
            <p><strong>Email:</strong> {selectedClient.email || 'Not provided'}</p>
            <p><strong>Emergency Contact:</strong> {selectedClient.emergency_contact_name || 'Not provided'}</p>
            <p><strong>Emergency Phone:</strong> {selectedClient.emergency_contact_phone || 'Not provided'}</p>
          </div>
          <div>
            <p><strong>Billing Address:</strong> {selectedClient.billing_address || 'Not provided'}</p>
            <p><strong>Billing Email:</strong> {selectedClient.billing_email || 'Not provided'}</p>
            <p><strong>Diagnosis Code:</strong> {selectedClient.diagnosis_code || 'Not provided'}</p>
            <p><strong>Is Minor:</strong> {selectedClient.is_minor ? 'Yes' : 'No'}</p>
            {selectedClient.is_minor && (
              <>
                <p><strong>Guardian Name:</strong> {selectedClient.guardian_name || 'Not provided'}</p>
                <p><strong>Guardian Phone:</strong> {selectedClient.guardian_phone || 'Not provided'}</p>
              </>
            )}
          </div>
        </div>
        
        {/* Client Statistics */}
        <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: 'var(--surface-muted)', borderRadius: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4>Client Statistics</h4>
            <button
              onClick={runDiagnostic}
              style={{
                backgroundColor: 'var(--accent-color)',
                color: 'black',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Debug Billing
            </button>
          </div>
          {isLoadingStats ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
              Loading statistics...
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                  {clientStats.totalSessions || 0}
                </div>
                <div>Total Sessions</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success-color)' }}>
                  {formatCurrency(clientStats.totalBilled || 0)}
                </div>
                <div>Total Billed</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffc107' }}>
                  {formatCurrency(clientStats.totalCollected || 0)}
                </div>
                <div>Total Collected</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: (clientStats.outstandingBalance || 0) > 0 ? 'var(--error-color)' : 'var(--success-color)' }}>
                  {formatCurrency(clientStats.outstandingBalance || 0)}
                </div>
                <div>Outstanding Balance</div>
              </div>
            </div>
          )}
          
          {/* Additional stats row */}
          {!isLoadingStats && clientStats && Object.keys(clientStats).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', textAlign: 'center', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#17a2b8' }}>
                  {clientStats.paidSessions || 0}
                </div>
                <div>Paid Sessions</div>
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--error-color)' }}>
                  {clientStats.unpaidSessions || 0}
                </div>
                <div>Unpaid Sessions</div>
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fd7e14' }}>
                  {clientStats.debtSessions || 0}
                </div>
                <div>Debt Sessions</div>
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--text-muted)' }}>
                  {clientStats.paymentRate ? `${clientStats.paymentRate}%` : '0%'}
                </div>
                <div>Payment Rate</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBillingHistory = () => (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3>Billing History</h3>
        <button 
          onClick={generateInvoice}
          style={{
            backgroundColor: 'var(--success-color)',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Generate Invoice
        </button>
      </div>
      
      {/* Filters */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
        <div>
          <label>Start Date:</label>
          <input
            type="date"
            value={billingFilters.startDate}
            onChange={(e) => setBillingFilters(prev => ({ ...prev, startDate: e.target.value }))}
            style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
          />
        </div>
        <div>
          <label>End Date:</label>
          <input
            type="date"
            value={billingFilters.endDate}
            onChange={(e) => setBillingFilters(prev => ({ ...prev, endDate: e.target.value }))}
            style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
          />
        </div>
        <div>
          <label>Status:</label>
          <select
            value={billingFilters.status}
            onChange={(e) => setBillingFilters(prev => ({ ...prev, status: e.target.value }))}
            style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
          >
            <option value="ALL">All</option>
            <option value="PAID">Paid</option>
            <option value="UNPAID">Unpaid</option>
            <option value="DEBT">Debt</option>
            <option value="SUBMITTED">Submitted</option>
          </select>
        </div>
        <button 
          onClick={() => fetchClientBillingHistory(true)}
          style={{
            backgroundColor: 'var(--primary-color)',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Filter
        </button>
      </div>
      
      {/* Billing Table */}
      {billingHistory.length === 0 ? (
        <p>No billing history found for this client.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--surface-muted)' }}>
                  <th style={{ padding: '0.75rem', border: '1px solid var(--border-color)', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '0.75rem', border: '1px solid var(--border-color)', textAlign: 'left' }}>Therapist</th>
                  <th style={{ padding: '0.75rem', border: '1px solid var(--border-color)', textAlign: 'left' }}>Service Type</th>
                  <th style={{ padding: '0.75rem', border: '1px solid var(--border-color)', textAlign: 'left' }}>Billed</th>
                  <th style={{ padding: '0.75rem', border: '1px solid var(--border-color)', textAlign: 'left' }}>Collected</th>
                  <th style={{ padding: '0.75rem', border: '1px solid var(--border-color)', textAlign: 'left' }}>Payment Type</th>
                  <th style={{ padding: '0.75rem', border: '1px solid var(--border-color)', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.75rem', border: '1px solid var(--border-color)', textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory.map((session) => (
                  <tr key={session.id}>
                    <td style={{ padding: '0.75rem', border: '1px solid var(--border-color)' }}>
                      {formatSessionDate(session.session_date)}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid var(--border-color)' }}>
                      {session.therapist_first_name} {session.therapist_last_name}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid var(--border-color)' }}>
                      {session.service_type || 'Session'}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid var(--border-color)' }}>
                      {formatCurrency(session.billed_amount)}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid var(--border-color)' }}>
                      {formatCurrency(session.collected_amount)}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid var(--border-color)' }}>
                      {session.payment_type && (
                        <span style={getPaymentStyle(session.payment_type)}>
                          {session.payment_type}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid var(--border-color)' }}>
                      <span style={getStatusBadge(session.status)}>
                        {session.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid var(--border-color)' }}>
                      {session.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {hasBillingMore && (
            <button 
              onClick={() => {
                setBillingPage(prev => prev + 1);
                fetchClientBillingHistory(false);
              }}
              style={{
                marginTop: '1rem',
                backgroundColor: 'var(--text-muted)',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Load More
            </button>
          )}
        </>
      )}
    </div>
  );

  const renderClientForms = () => (
    <div style={cardStyle}>
      <h3>Client Forms</h3>
      {localForms.length === 0 ? (
        <p>No forms found for this client</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {localForms.map((form) => {
            const formType = determineFormType(form);
            const formDate = formatSessionDate(form.created_at);
            
            return (
              <button
                key={form.id}
                onClick={() => handleViewForm(form)}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: 'var(--surface-muted)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  color: '#212529',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = 'var(--border-color)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'var(--surface-muted)';
                }}
              >
                {formType} - {formDate}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderClientEditor = () => (
    <>
      <div style={overlayStyle} onClick={() => setShowClientEditor(false)} />
      <div style={modalStyle}>
        <h3>Edit Client Information</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label>Client Name *</label>
            <input
              type="text"
              value={editedClientData.clientName}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, clientName: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
              required
            />
          </div>
          
          <div>
            <label>Phone</label>
            <input
              type="tel"
              value={editedClientData.phone}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, phone: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Email</label>
            <input
              type="email"
              value={editedClientData.email}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, email: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Emergency Contact Name</label>
            <input
              type="text"
              value={editedClientData.emergencyContactName}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, emergencyContactName: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Emergency Contact Phone</label>
            <input
              type="tel"
              value={editedClientData.emergencyContactPhone}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, emergencyContactPhone: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Diagnosis Code</label>
            <input
              type="text"
              value={editedClientData.diagnosisCode}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, diagnosisCode: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Billing Email</label>
            <input
              type="email"
              value={editedClientData.billingEmail}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, billingEmail: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          
          <div>
            <label>Billing Address</label>
            <input
              type="text"
              value={editedClientData.billingAddress}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, billingAddress: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label>
            <input
              type="checkbox"
              checked={editedClientData.isMinor}
              onChange={(e) => setEditedClientData(prev => ({ ...prev, isMinor: e.target.checked }))}
              style={{ marginRight: '0.5rem' }}
            />
            Is Minor
          </label>
        </div>
        
        {editedClientData.isMinor && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label>Guardian Name</label>
              <input
                type="text"
                value={editedClientData.guardianName}
                onChange={(e) => setEditedClientData(prev => ({ ...prev, guardianName: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
              />
            </div>
            
            <div>
              <label>Guardian Phone</label>
              <input
                type="tel"
                value={editedClientData.guardianPhone}
                onChange={(e) => setEditedClientData(prev => ({ ...prev, guardianPhone: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
              />
            </div>
          </div>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button 
            onClick={() => setShowClientEditor(false)}
            style={{
              backgroundColor: 'var(--text-muted)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={saveClientEdits}
            style={{
              backgroundColor: 'var(--success-color)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div style={containerStyle}>
      {/* Sidebar - Client Search */}
      <div style={sidebarStyle}>
        <h3>Client Search</h3>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, phone, or therapist..."
          style={{
            width: '100%',
            padding: '0.75rem',
            marginBottom: '0.5rem',
            border: '1px solid var(--border-color)',
            borderRadius: '4px'
          }}
        />
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            onClick={() => setShowMergeModal(true)}
            style={{
              flex: 1,
              padding: '0.6rem',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              background: 'var(--surface-muted, #f3f4f6)',
              color: '#1f2937',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            ⇋ Merge duplicates
          </button>
          <button
            onClick={() => setShowMergeHistory(true)}
            title="Merge history"
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              background: '#fff',
              color: '#1f2937',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            🕘
          </button>
        </div>

        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {displayedClients.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>
              No clients found
            </div>
          ) : (
            displayedClients.map((client) => {
              const therapistName = getClientTherapist(client);
              
              return (
                <div
                  key={client.id}
                  style={clientListItemStyle(selectedClient && selectedClient.id === client.id)}
                  onClick={() => handleSelectClient(client)}
                >
                  <div style={{ fontWeight: 'bold' }}>
                    {client.client_name}
                    {therapistName && (
                      <span style={{ fontWeight: 'normal', opacity: 0.8 }}>
                        {' '}({therapistName})
                      </span>
                    )}
                  </div>
                  {client.email && <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>{client.email}</div>}
                  {client.phone && <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>{client.phone}</div>}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={mainContentStyle}>
        {!selectedClient ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
            <h3>Select a client to view their profile</h3>
            <p>Use the search bar on the left to find and select a client.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ marginBottom: '2rem' }}>
              <h2>
                {selectedClient.client_name}
                {(() => {
                  const therapistName = getClientTherapist(selectedClient);
                  return therapistName ? ` (${therapistName})` : '';
                })()}
              </h2>
              <div style={{ color: 'var(--text-muted)' }}>
                Client ID: {selectedClient.id} | 
                {selectedClient.email && ` Email: ${selectedClient.email} |`}
                {selectedClient.phone && ` Phone: ${selectedClient.phone}`}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ marginBottom: '1rem' }}>
              <button 
                style={tabStyle(activeTab === 'overview')}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button 
                style={tabStyle(activeTab === 'billing')}
                onClick={() => setActiveTab('billing')}
              >
                Billing History
              </button>
              <button
                style={tabStyle(activeTab === 'forms')}
                onClick={() => setActiveTab('forms')}
              >
                Forms
              </button>
              {hasArea('payment_processing') && (
                <button
                  style={tabStyle(activeTab === 'cards')}
                  onClick={() => setActiveTab('cards')}
                >
                  Cards on File
                </button>
              )}
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && renderClientOverview()}
            {activeTab === 'billing' && renderBillingHistory()}
            {activeTab === 'forms' && renderClientForms()}
            {activeTab === 'cards' && hasArea('payment_processing') && (
              <CardsOnFilePanel client={selectedClient} />
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showClientEditor && renderClientEditor()}
      
      {/* Diagnostic Modal */}
      {showDiagnostic && diagnosticData && (
        <>
          <div style={overlayStyle} onClick={() => setShowDiagnostic(false)} />
          <div style={{...modalStyle, width: '95%', maxWidth: '1200px'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Billing Analysis for {selectedClient.client_name}</h3>
              <button 
                onClick={() => setShowDiagnostic(false)}
                style={{
                  backgroundColor: 'var(--text-muted)',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
            
            {/* Summary */}
            <div style={{ backgroundColor: 'var(--surface-muted)', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
              <h4>Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div><strong>Total Sessions:</strong> {diagnosticData.summary?.totalSessions || 0}</div>
                <div><strong>Total Billed:</strong> {formatCurrency(diagnosticData.summary?.totalBilled || 0)}</div>
                <div><strong>Total Collected:</strong> {formatCurrency(diagnosticData.summary?.totalCollected || 0)}</div>
                <div><strong>Outstanding:</strong> {formatCurrency(diagnosticData.summary?.outstandingBalance || 0)}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                <div><strong>Paid Sessions:</strong> {diagnosticData.summary?.sessionsByStatus?.PAID || 0}</div>
                <div><strong>Unpaid Sessions:</strong> {diagnosticData.summary?.sessionsByStatus?.UNPAID || 0}</div>
                <div><strong>Debt Sessions:</strong> {diagnosticData.summary?.sessionsByStatus?.DEBT || 0}</div>
                <div><strong>Payment Rate:</strong> {diagnosticData.summary?.paymentRate || 0}%</div>
              </div>
            </div>
            
            {/* Billing Issues */}
            {diagnosticData.billingIssues && diagnosticData.billingIssues.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ color: 'var(--error-color)' }}>Billing Issues Found ({diagnosticData.billingIssues.length})</h4>
                <div style={{ overflowX: 'auto', maxHeight: '200px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--error-color)', color: 'white' }}>
                        <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Issue Type</th>
                        <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Status</th>
                        <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Billed</th>
                        <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Collected</th>
                        <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Description</th>
                        <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Therapist</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnosticData.billingIssues.map((issue, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>
                            {formatSessionDate(issue.date)}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)', fontWeight: 'bold' }}>
                            {issue.issueType}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>{issue.status}</td>
                          <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>
                            {formatCurrency(issue.billed)}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>
                            {formatCurrency(issue.collected)}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>{issue.description}</td>
                          <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>{issue.therapist}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Potential Duplicates */}
            {diagnosticData.potentialDuplicates && diagnosticData.potentialDuplicates.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ color: '#ffc107' }}>Potential Duplicate Sessions ({diagnosticData.potentialDuplicates.length})</h4>
                <div style={{ overflowX: 'auto', maxHeight: '200px' }}>
                  {diagnosticData.potentialDuplicates.map((duplicate, idx) => (
                    <div key={idx} style={{ 
                      backgroundColor: '#fff3cd', 
                      border: '1px solid #ffeaa7', 
                      padding: '0.75rem', 
                      marginBottom: '0.5rem',
                      borderRadius: '4px'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        Date: {duplicate.date} - {duplicate.reason}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        {duplicate.sessions.map((session, sessionIdx) => (
                          <div key={sessionIdx} style={{ 
                            backgroundColor: 'white', 
                            padding: '0.5rem', 
                            borderRadius: '4px',
                            border: '1px solid var(--border-color)'
                          }}>
                            <div><strong>Session ID:</strong> {session.id}</div>
                            <div><strong>Billed:</strong> {formatCurrency(session.billed)}</div>
                            <div><strong>Collected:</strong> {formatCurrency(session.collected)}</div>
                            <div><strong>Status:</strong> {session.status}</div>
                            <div><strong>Therapist:</strong> {session.therapist}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                              Created: {new Date(session.createdAt).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* All Sessions */}
            <div>
              <h4>All Sessions ({diagnosticData.allSessions?.length || 0})</h4>
              <div style={{ overflowX: 'auto', maxHeight: '400px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--surface-muted)' }}>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Date</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Status</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Service</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Billed</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Collected</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Difference</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Payment Type</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Therapist</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnosticData.allSessions && diagnosticData.allSessions.map((session, idx) => (
                      <tr key={idx} style={{ 
                        backgroundColor: Math.abs(session.difference) > 0.01 ? '#ffebee' : 'white' 
                      }}>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>
                          {formatSessionDate(session.date)}
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>
                          <span style={getStatusBadge(session.status)}>
                            {session.status}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>{session.serviceType}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>
                          {formatCurrency(session.billed)}
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>
                          {formatCurrency(session.collected)}
                        </td>
                        <td style={{ 
                          padding: '0.5rem', 
                          border: '1px solid var(--border-color)',
                          color: Math.abs(session.difference) > 0.01 ? 'var(--error-color)' : 'var(--success-color)',
                          fontWeight: Math.abs(session.difference) > 0.01 ? 'bold' : 'normal'
                        }}>
                          {formatCurrency(session.difference)}
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>
                          {session.paymentType && (
                            <span style={getPaymentStyle(session.paymentType)}>
                              {session.paymentType}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)' }}>{session.therapist}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--border-color)', maxWidth: '200px', wordBreak: 'break-word' }}>
                          {session.notes}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div style={{ marginTop: '1rem', padding: '0.5rem', backgroundColor: 'var(--border-color)', borderRadius: '4px', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              Analysis generated on: {diagnosticData.analysisDate ? new Date(diagnosticData.analysisDate).toLocaleString() : 'Unknown'}
            </div>
          </div>
        </>
      )}
      
      {/* Add other modals for forms, invoices, etc. as needed */}

      {showAddressLinkModal && selectedClient && (
        <AddressUpdateLinkModal
          client={selectedClient}
          sendPath="/forms/address-update/generate"
          onClose={() => setShowAddressLinkModal(false)}
        />
      )}

      {showUpdateRequestModal && selectedClient && (
        <ProfileUpdateRequestModal
          client={selectedClient}
          onClose={() => setShowUpdateRequestModal(false)}
          onSent={() => fetchUpdateRequestStatus(selectedClient.id)}
        />
      )}

      {showMergeModal && (
        <MergeClientsModal
          allClients={allClients}
          onClose={() => setShowMergeModal(false)}
          onMerged={() => {
            setShowMergeModal(false);
            setSelectedClient(null);
            fetchAllPortalClients();
          }}
        />
      )}

      {showMergeHistory && (
        <MergeHistoryModal onClose={() => setShowMergeHistory(false)} />
      )}
    </div>
  );
}

/* ============================================================
   Merge Clients Modal

   Two phases:
   1) Select a primary (keeper) + one or more duplicates.
   2) Preview: shows what will move per duplicate + a field-by-field
      chooser for any clients-row field that differs. Confirm merges.
============================================================ */
const mergeBlank = (v) => v === null || v === undefined || v === '';
const mergeDisplay = (v) => {
  if (mergeBlank(v)) return '(blank)';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.split('T')[0];
  return s;
};
const mergeKey = (v) => (mergeBlank(v) ? '__blank__' : String(v));
const prettyField = (f) =>
  f.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function MergeClientsModal({ allClients, onClose, onMerged }) {
  const [search, setSearch] = useState('');
  const [primaryId, setPrimaryId] = useState(null);
  const [dupIds, setDupIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [fieldChoices, setFieldChoices] = useState({});
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return allClients
      .filter((c) => (c.client_name || '').toLowerCase().includes(q) || String(c.id) === q)
      .slice(0, 50);
  }, [search, allClients]);

  const nameOf = (id) => {
    const c = allClients.find((x) => x.id === id);
    return c ? c.client_name : `#${id}`;
  };

  const choosePrimary = (id) => {
    setPrimaryId(id);
    setDupIds((prev) => prev.filter((x) => x !== id));
  };
  const toggleDup = (id) => {
    if (id === primaryId) return;
    setDupIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const candidatesFor = (field) => {
    const list = [{ value: preview.primary.fields[field], label: `Primary #${preview.primary.id}` }];
    preview.duplicates.forEach((d) => list.push({ value: d.fields[field], label: `#${d.id}` }));
    // Dedupe by value, combining source labels.
    const seen = new Map();
    list.forEach((c) => {
      const k = mergeKey(c.value);
      if (!seen.has(k)) seen.set(k, { value: c.value, labels: [c.label] });
      else seen.get(k).labels.push(c.label);
    });
    return [...seen.values()];
  };

  const differingFields = useMemo(() => {
    if (!preview) return [];
    return preview.fieldList.filter((f) => {
      const vals = [preview.primary.fields[f], ...preview.duplicates.map((d) => d.fields[f])].map(mergeKey);
      return new Set(vals).size > 1;
    });
  }, [preview]);

  const runPreview = async () => {
    setError('');
    if (!primaryId) { setError('Pick a primary (keeper) client.'); return; }
    if (dupIds.length === 0) { setError('Pick at least one duplicate to merge in.'); return; }
    setLoading(true);
    try {
      const res = await API.post('/admin/clients/merge-preview', { primaryId, duplicateIds: dupIds });
      setPreview(res.data);
      const choices = {};
      res.data.fieldList.forEach((f) => {
        const cands = [res.data.primary.fields[f], ...res.data.duplicates.map((d) => d.fields[f])];
        let chosen = cands[0];
        if (mergeBlank(chosen)) {
          const firstNonBlank = cands.find((v) => !mergeBlank(v));
          if (firstNonBlank !== undefined) chosen = firstNonBlank;
        }
        choices[f] = chosen;
      });
      setFieldChoices(choices);
    } catch (e) {
      setError(e?.response?.data?.message || 'Error building preview.');
    } finally {
      setLoading(false);
    }
  };

  const confirmMerge = async () => {
    setMerging(true);
    setError('');
    try {
      const fieldValues = {};
      differingFields.forEach((f) => { fieldValues[f] = fieldChoices[f]; });
      await API.post('/admin/clients/merge', { primaryId, duplicateIds: dupIds, fieldValues });
      alert('Clients merged successfully.');
      onMerged();
    } catch (e) {
      setError(e?.response?.data?.message || 'Error merging clients.');
      setMerging(false);
    }
  };

  return (
    <div style={mergeBackdrop} onClick={onClose}>
      <div style={mergeCard} onClick={(e) => e.stopPropagation()}>
        <div style={mergeHeader}>
          <h3 style={{ margin: 0 }}>Merge duplicate clients</h3>
          <button onClick={onClose} style={mergeCloseBtn} aria-label="Close">×</button>
        </div>

        <div style={{ padding: '1rem', overflowY: 'auto' }}>
          {error && <div style={mergeError}>{error}</div>}

          {!preview ? (
            <>
              <p style={{ marginTop: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Search for the duplicated client, choose which profile to <strong>keep</strong> (primary),
                and check the duplicate(s) to fold into it.
              </p>
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients by name or id…"
                style={{ width: '100%', padding: '0.6rem', border: '1px solid var(--border-color)', borderRadius: '4px', marginBottom: '0.75rem' }}
              />
              <div style={mergeListBox}>
                <div style={{ display: 'grid', gridTemplateColumns: '54px 60px 1fr', padding: '4px 8px', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                  <div>Keep</div><div>Merge</div><div>Client</div>
                </div>
                {search.trim() === '' ? (
                  <div style={{ padding: '0.75rem', color: 'var(--text-muted)' }}>Type to search…</div>
                ) : filtered.length === 0 ? (
                  <div style={{ padding: '0.75rem', color: 'var(--text-muted)' }}>No matches.</div>
                ) : (
                  filtered.map((c) => (
                    <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '54px 60px 1fr', alignItems: 'center', padding: '6px 8px', borderTop: '1px solid var(--border-color)' }}>
                      <input type="radio" name="mergePrimary" checked={primaryId === c.id} onChange={() => choosePrimary(c.id)} />
                      <input type="checkbox" checked={dupIds.includes(c.id)} disabled={primaryId === c.id} onChange={() => toggleDup(c.id)} />
                      <div>{c.client_name} <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>#{c.id}</span></div>
                    </div>
                  ))
                )}
              </div>

              <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                {primaryId ? <>Keeping <strong>{nameOf(primaryId)} #{primaryId}</strong></> : 'No primary selected'}
                {dupIds.length > 0 && <> · merging {dupIds.length} duplicate(s): {dupIds.map((id) => `#${id}`).join(', ')}</>}
              </div>

              <div style={mergeFooter}>
                <button onClick={onClose} style={mergeBtn}>Cancel</button>
                <button onClick={runPreview} style={mergeBtnPrimary} disabled={loading}>
                  {loading ? 'Loading…' : 'Preview merge →'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Keeping:</strong> {preview.primary.name} (#{preview.primary.id})
              </div>

              {/* What moves */}
              <h4 style={{ margin: '0 0 0.5rem' }}>Records that will move</h4>
              {preview.duplicates.map((d) => {
                const nonzero = Object.entries(d.counts).filter(([, n]) => n > 0);
                return (
                  <div key={d.id} style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    <div><strong>{d.name} (#{d.id})</strong>{d.hasPaymentProfile && <span style={mergeCardWarn}>has card on file</span>}</div>
                    <div style={{ color: 'var(--text-muted)' }}>
                      {nonzero.length === 0 ? 'No linked records.' : nonzero.map(([t, n]) => `${prettyField(t)}: ${n}`).join(' · ')}
                    </div>
                  </div>
                );
              })}

              {/* Field chooser */}
              <h4 style={{ margin: '1rem 0 0.5rem' }}>Choose which value to keep</h4>
              {differingFields.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  All profile fields already match — nothing to choose.
                </p>
              ) : (
                differingFields.map((f) => (
                  <div key={f} style={{ padding: '0.5rem 0', borderTop: '1px solid var(--border-color)' }}>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{prettyField(f)}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {candidatesFor(f).map((cand, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', background: mergeKey(fieldChoices[f]) === mergeKey(cand.value) ? '#eff6ff' : '#fff' }}>
                          <input
                            type="radio"
                            name={`field-${f}`}
                            checked={mergeKey(fieldChoices[f]) === mergeKey(cand.value)}
                            onChange={() => setFieldChoices((prev) => ({ ...prev, [f]: cand.value }))}
                          />
                          <span>{mergeDisplay(cand.value)}</span>
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>({cand.labels.join(', ')})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}

              <div style={mergeFooter}>
                <button onClick={() => setPreview(null)} style={mergeBtn} disabled={merging}>← Back</button>
                <button onClick={confirmMerge} style={mergeBtnDanger} disabled={merging}>
                  {merging ? 'Merging…' : `Merge ${dupIds.length} into #${primaryId}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Merge History Modal — read-only audit of client merges.
============================================================ */
function MergeHistoryModal({ onClose }) {
  const [merges, setMerges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await API.get('/admin/client-merges?limit=100');
        if (active) setMerges(res.data.merges || []);
      } catch (e) {
        if (active) setError(e?.response?.data?.message || 'Error loading merge history.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div style={mergeBackdrop} onClick={onClose}>
      <div style={mergeCard} onClick={(e) => e.stopPropagation()}>
        <div style={mergeHeader}>
          <h3 style={{ margin: 0 }}>Merge history</h3>
          <button onClick={onClose} style={mergeCloseBtn} aria-label="Close">×</button>
        </div>
        <div style={{ padding: '1rem', overflowY: 'auto' }}>
          {error && <div style={mergeError}>{error}</div>}
          {loading ? (
            <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
          ) : merges.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>No merges recorded yet.</p>
          ) : (
            merges.map((m) => {
              const moved = m.moved_counts && typeof m.moved_counts === 'object'
                ? Object.entries(m.moved_counts).filter(([, n]) => n > 0)
                : [];
              return (
                <div key={m.id} style={{ padding: '0.6rem 0', borderTop: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {new Date(m.created_at).toLocaleString()}{m.admin_name ? ` · ${m.admin_name}` : ''}
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {m.duplicate_name} (#{m.duplicate_client_id}) → {m.primary_name} (#{m.primary_client_id})
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    {moved.length === 0
                      ? 'No linked records moved.'
                      : moved.map(([t, n]) => `${prettyField(t)}: ${n}`).join(' · ')}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div style={{ ...mergeFooter, padding: '0 1rem 1rem', marginTop: 0 }}>
          <button onClick={onClose} style={mergeBtn}>Close</button>
        </div>
      </div>
    </div>
  );
}

const mergeBackdrop = { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' };
const mergeCard = { background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '680px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' };
const mergeHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-color)' };
const mergeCloseBtn = { background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1 };
const mergeListBox = { border: '1px solid var(--border-color)', borderRadius: '6px', maxHeight: '260px', overflowY: 'auto' };
const mergeFooter = { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' };
const mergeBtn = { padding: '8px 14px', border: '1px solid var(--border-color)', borderRadius: '6px', background: '#fff', color: '#1f2937', cursor: 'pointer', fontWeight: 500 };
const mergeBtnPrimary = { ...mergeBtn, background: 'var(--accent-color)', color: '#fff', borderColor: '#2563eb' };
const mergeBtnDanger = { ...mergeBtn, background: 'var(--error-color)', color: '#fff', borderColor: 'var(--error-color)' };
const mergeError = { background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.6rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.9rem' };
const mergeCardWarn = { marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: '999px', padding: '2px 8px' };

export default AdminClientProfilePage;